import { createRef, PureComponent } from "react";
import { velvet, VelvetColors, VelvetConfiguration } from "@app/velvet";
import { TrendChartItemType } from "@app/velvet/enumerators";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { AttestationRateDays, TimeFrequency } from "@app/projects/enumerators";
import { getTimeString } from "@app/projects/network-assessment/adequacy/adequacyUtilities";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";

interface VelvetProps {
  attestationRateDays?: AttestationRateDays;
  config: VelvetConfiguration;
  projectRunConfigurations: ProjectRunConfiguration[];
}

class WrappedVelvet extends PureComponent<VelvetProps & FeatureFlagContextProps> {
  private svg = createRef<SVGSVGElement>();

  public componentDidMount() {
    velvet(this.svg.current, this.props.config);
  }

  public componentDidUpdate() {
    velvet(this.svg.current, this.props.config);
  }

  public render() {
    const title = `${this.props.config.module} Trend Graph`;
    const description = this.getVelvetAltText();
    return (
      <svg className="velvet-trend-chart" ref={this.svg} role="img" aria-label={`${title}. ${description}`}>
        <title>{title}</title>
        <desc>{description}</desc>
      </svg>
    );
  }

  private getVelvetAltText() {
    if (!this.props.config.items) {
      return "";
    }

    let baseString = `A complex chart illustrating data trends from this project over the last ${this.props.config.items.length}`;
    baseString += ` ${this.getSingularOrPluralString("time", "times", this.props.config.items.length)} it was run.`;
    if (!this.props.config.options.showAdvancedComparison) {
      baseString += ` There is text that reads, "Trending is not licensed."`;
    }

    switch (this.props.config.module) {
      case "Accuracy":
        // accuracy's bar colors are pulled from their templates, so we just have to read the barGroups
        const labels: { [color: string]: string } = {};
        this.props.config.labels.forEach((label) => {
          labels[label.color] = label.name;
        });
        this.props.config.items.forEach((item) => {
          const thisConfig = this.props.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === item.projectRunConfigurationId);
          if (!thisConfig) {
            return;
          }
          const isSelected = item.projectRunConfigurationId === this.props.config.projectRunConfigurationId;
          const timeString = getTimeString(thisConfig, item.runFrequency);
          // the api sends the total provider count for each accuracy bar and we calculate the percentages on the ui
          const totalCount = item.barGroups[0].items.reduce((total, current) => {
            return total += current.value;
          }, 0);
          if (isSelected) {
            baseString += ` The following time slice is the currently selected time slice.`
          }
          baseString += ` ${this.getOpeningClause(item.runFrequency, item.itemType, timeString)} this project's data was`;
          item.barGroups[0].items.forEach((bar, index, arr) => {
            if (index > 0) {
              baseString += ",";
            }
            if (index === arr.length - 1) {
              baseString += " and"
            }
            baseString += ` ${this.getAccuracyPercentageString(totalCount, bar.value)} ${labels[bar.color]}`;
          });
          const accuracyIndex = item.linePoints.find((x) => x.color === "#00a46c").value;
          baseString += `. It had an accuracy index of ${accuracyIndex}`;

          if (this.props.config.options.showAttestation) {
            const percentAttested = item.linePoints.find((x) => x.color === VelvetColors.Blue).value;
            baseString += ` and a ${percentAttested}% attestation rate`;
            baseString += ` based on a ${this.props.attestationRateDays || 365}-day attestation rate`
          }

          baseString += ".";
        });
        break;
      case "Adequacy":
        const isDualMembership = this.props.config.isDualMembership && this.props.config.supplementaryItems?.length > 0;
        this.props.config.items.forEach((item, index) => {
          const thisConfig = this.props.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === item.projectRunConfigurationId);
          if (!thisConfig) {
            return;
          }
          const isSelected = item.projectRunConfigurationId === this.props.config.projectRunConfigurationId;
          const isCompareSelected = item.projectRunConfigurationId === this.props.config.compareToRunConfigurationId;
          const isUnpublished = !thisConfig.datePublished || item.itemType === TrendChartItemType.Unpublished;
          const timeString = getTimeString(thisConfig, item.runFrequency, new Date(item.timestamp.local().format()));

          if (item.disabled) {
            baseString += ` There is no data for ${timeString} with the selected filters.`;
            return;
          }

          // barGroups is always an array of 1
          // we don't currently show exceptions in these bars, probably because there are multiple types
          const yellowBar = item.barGroups[0].items.find((x) => x.color === VelvetColors.Yellow);
          const redBar = item.barGroups[0].items.find((x) => x.color === VelvetColors.Red);
          // we don't currently have membership aliases in the trend chart
          const prefix = isDualMembership ? "the primary membership" : "this project";

          if (isSelected) {
            baseString += " The following time slice is the currently selected time slice.";
          }
          if (isCompareSelected) {
            baseString += " The following time slice is the currently selected comparison time slice.";
          }
          if (isUnpublished) {
            baseString += " The following time slice is not yet published.";
          }
          baseString += ` ${this.getOpeningClause(item.runFrequency, item.itemType, timeString)}`;
          baseString += ` ${prefix} had ${yellowBar.value} ${this.getSingularOrPluralString("gap", "gaps", yellowBar.value)} that only met one of access or servicing requirements,`
          baseString += ` ${redBar.value} ${this.getSingularOrPluralString("gap", "gaps", redBar.value)} that did not meet any adequacy requirements`;

          if (this.props.config.options.showAttestation) {
            const percentAttested = item.linePoints.find((x) => x.color === VelvetColors.Blue).value;
            // if percentAttested is undefined or null we hide it, but we do show 0
            if (percentAttested !== undefined && percentAttested !== null) {
              // as of this implementation, adequacy should always show 365 regardless of this.props.attestationRateDays
              baseString += `, a ${percentAttested}% attestation rate based on a 365-day attestation rate`;
            }
          }
          const adequacyIndex = item.linePoints.find((x) => x.color === VelvetColors.Green).value;
          baseString += `, and an adequacy index of ${adequacyIndex}.`

          if (isDualMembership && !!this.props.config.supplementaryItems && !!this.props.config.supplementaryItems[index]) {
            const yellowBar2 = this.props.config.supplementaryItems[index].barGroups[0].items.find((x) => x.color === VelvetColors.Yellow);
            const redBar2 = this.props.config.supplementaryItems[index].barGroups[0].items.find((x) => x.color === VelvetColors.Red);
            baseString += ` The secondary membership had ${yellowBar2.value} ${this.getSingularOrPluralString("gap", "gaps", yellowBar2.value)} that only met one of access or servicing requirements,`
            baseString += ` ${redBar2.value} ${this.getSingularOrPluralString("gap", "gaps", redBar2.value)} that did not meet any adequacy requirements`;

            if (this.props.config.options.showAttestation) {
              const percentAttested2 = this.props.config.supplementaryItems[index].linePoints.find((x) => x.color === VelvetColors.BlueLight).value;
              if (percentAttested2 !== undefined && percentAttested2 !== null) {
                // as of this implementation, adequacy should always show 365 regardless of this.props.attestationRateDays
                baseString += `, a ${percentAttested2}% attestation rate based on a 365-day attestation rate`;
              }
            }
            const adequacyIndex2 = this.props.config.supplementaryItems[index].linePoints.find((x) => x.color === VelvetColors.GreenDark).value;
            baseString += `, and an adequacy index of ${adequacyIndex2}.`
          }
        });
        break;
    }

    return baseString;
  }

  private getSingularOrPluralString(singular: string, plural: string, count: number) {
    if (count === 1) {
      return singular;
    }
    return plural;
  }

  private getOpeningClause(runFrequency: TimeFrequency, itemType: TrendChartItemType, timeString: string) {
    switch (itemType) {
      case TrendChartItemType.Projected:
      case TrendChartItemType.Termed:
        return ` In the ${itemType} time slice,`;
      case TrendChartItemType.ProjectedTermed:
        return " In the projected-termed time slice,";
      default:
        const preposition = runFrequency === TimeFrequency.All ? "On" : "In";
        return ` ${preposition} ${timeString},`;
    }
  }

  private getAccuracyPercentageString(totalCount: number, currentCount: number) {
    const value = currentCount / totalCount;
    let valueString: string;
    if (value > 0 && value < 0.01) {
      valueString = "less than 0.1%";
    } else {
      valueString = `${(Math.round(value * 100))}%`;
    }

    return valueString;
  }
}

const Velvet = withFeatureFlagContext(WrappedVelvet);
export default Velvet;
